import {
    Button,
    DisplayOrInput,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    Field,
} from "modules/common";
import { useEffect, useMemo, useState } from "react";
import { EducationDocumentCategory } from "services/educationDocuments/response";
import {
    useCreateEducationDocumentCategory,
    useDeleteEducationDocumentCategory,
    useEditEducationDocumentCategory,
} from "../hooks";
import { ConfirmationModal } from "modules/common/components/ConfirmationModal";

export interface IEducationDocumentCategoryModal {
    educationDocumentCategory?: EducationDocumentCategory;
    isOpen: boolean;
    handleClose: () => void;
    hasDocuments?: boolean;
}

const emptyEducationDocumentCategory: EducationDocumentCategory = {
    guid: "",
    name: "",
    createdBy: "",
    created: "",
    lastUpdatedBy: "",
    lastUpdated: "",
};

type EducationDocumentCategoryProperty = keyof EducationDocumentCategory;

type EducationDocumentCategoryErrors = Partial<Record<EducationDocumentCategoryProperty, string>>;

const validateEducationDocumentCategory = (
    educationDocumentCategory: EducationDocumentCategory
): EducationDocumentCategoryErrors => {
    const errors: EducationDocumentCategoryErrors = {};
    if (!educationDocumentCategory.name.trim()) {
        errors.name = "Naziv kategorije je obavezan";
    }

    return errors;
};

export const EducationDocumentCategoryModal = ({
    educationDocumentCategory: selectedEducationDocumentCategory,
    isOpen,
    handleClose,
    hasDocuments,
}: IEducationDocumentCategoryModal) => {
    const [educationDocumentCategory, setEducationDocumentCategory] =
        useState<EducationDocumentCategory>(emptyEducationDocumentCategory);
    const [errors, setErrors] = useState<EducationDocumentCategoryErrors>({});
    const [isEdit, setIsEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const create = useCreateEducationDocumentCategory();
    const edit = useEditEducationDocumentCategory();
    const remove = useDeleteEducationDocumentCategory();

    useEffect(() => {
        setEducationDocumentCategory(
            selectedEducationDocumentCategory ?? emptyEducationDocumentCategory
        );
    }, [selectedEducationDocumentCategory]);

    useEffect(() => {
        setErrors({});
        setIsEdit(false);
    }, [isOpen]);

    const isAdd = useMemo(
        () => !selectedEducationDocumentCategory,
        [selectedEducationDocumentCategory]
    );

    const title = useMemo(() => {
        if (isAdd) return "Nova kategorija";
        if (isEdit) return "Uredi kategoriju";
        return educationDocumentCategory?.name ?? "Detalji kategorije";
    }, [isAdd, isEdit, educationDocumentCategory?.name]);

    const changeEducationDocumentCategoryProperty = (
        property: EducationDocumentCategoryProperty,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setErrors(prev => ({ ...prev, [property]: undefined }));
        setEducationDocumentCategory(prev => ({
            ...prev,
            [property]: event.target.value,
        }));
    };

    const isValid = (property: EducationDocumentCategoryProperty) => !errors[property];
    const getErrorMsg = (property: EducationDocumentCategoryProperty) => errors[property] ?? "";

    const onCancel = () => {
        setIsEdit(false);
        setErrors({});
        const category = selectedEducationDocumentCategory ?? emptyEducationDocumentCategory;
        setEducationDocumentCategory(category);
    };

    const onSave = async () => {
        const newErrors = validateEducationDocumentCategory(educationDocumentCategory);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        const mutation = isAdd ? create.mutateAsync : edit.mutateAsync;
        await mutation(educationDocumentCategory);
        handleClose();
    };

    const onDelete = async () => {
        if (!educationDocumentCategory) return;
        await remove.mutateAsync(educationDocumentCategory.guid);
        setShowDelete(false);
        handleClose();
    };

    return (
        <Modal
            isActive={isOpen}
            className="education-document-category-modal"
            onClose={handleClose}
            modalHeader={<ModalHeader onClose={handleClose} title={title} />}
            modalFooter={
                educationDocumentCategory ? (
                    <ModalFooter>
                        {isAdd || isEdit ? (
                            <>
                                {isEdit && (
                                    <Button
                                        type="button"
                                        size="small"
                                        variant="primary-ghost"
                                        onClick={onCancel}
                                    >
                                        <span>ODUSTANI</span>
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary"
                                    onClick={onSave}
                                >
                                    <span>SPREMI</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary-ghost"
                                    onClick={() => setIsEdit(true)}
                                >
                                    <span>UREDI</span>
                                </Button>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="warning-ghost"
                                    disabled={hasDocuments ?? false}
                                    onClick={() => setShowDelete(true)}
                                >
                                    <span>OBRIŠI</span>
                                </Button>
                                {hasDocuments && (
                                    <span className="error-message">
                                        Nije moguće obrisati kategoriju koja sadrži dokumentaciju.
                                    </span>
                                )}
                            </>
                        )}
                    </ModalFooter>
                ) : null
            }
        >
            <div className="education-document-category">
                <div className="modal-section">
                    <Field name="Naziv kategorije" errorMessage={getErrorMsg("name")}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {educationDocumentCategory?.name}
                            <Input
                                id="name"
                                type="text"
                                size="small"
                                name="name"
                                isValid={isValid("name")}
                                value={educationDocumentCategory?.name}
                                onChange={e => changeEducationDocumentCategoryProperty("name", e)}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
            </div>
            <ConfirmationModal
                isActive={showDelete}
                onConfirm={onDelete}
                onCancel={() => setShowDelete(false)}
                content={`Želite li izbrisati kategoriju ${educationDocumentCategory?.name}?`}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
            />
        </Modal>
    );
};
